<template>
  <div class="dashboard-container">
    <b-container fluid>
      <!-- Header Section -->
      <b-card
        no-body
        class="shadow-sm border-0 mb-4"
      >
        <b-card-body>
          <div>
            <h1 class="display-6 mb-0">
              <span class="text-primary">{{ $branding.company.shortName }}</span>
              <span class="badge bg-primary-subtle">🚀</span>
            </h1>
            <p>Statistique de livraison {{ getDateRangeText }}</p>
          </div>
          <div v-if="isLoading">
            <b-spinner
              variant="primary"
              label="Loading..."
            />
          </div>
        </b-card-body>
      </b-card>

      <!-- Filters Section -->
      <b-card
        no-body
        class="shadow-sm border-0 mb-4"
      >
        <b-card-body>
          <b-row align-v="center">
            <b-col md="6">
              <b-form-group
                label="Date de début"
                label-class="text-muted font-weight-bold"
              >
                <b-form-datepicker
                  v-model="fromDate"
                  :max="toDate || undefined"
                  locale="fr"
                  placeholder="Sélectionner une date"
                  class="date-picker-custom"
                  today-button
                  reset-button
                  close-button
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="Date de fin"
                label-class="text-muted font-weight-bold"
              >
                <b-form-datepicker
                  v-model="toDate"
                  :min="fromDate || undefined"
                  locale="fr"
                  placeholder="Sélectionner une date"
                  class="date-picker-custom"
                  today-button
                  reset-button
                  close-button
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="mt-3">
            <b-col class="d-flex justify-content-end">
              <b-button
                variant="primary"
                class="mr-2"
                @click="applyFilters"
              >
                <i class="fas fa-filter mr-2" />
                Appliquer les filtres
              </b-button>
              <b-button
                variant="outline-secondary"
                @click="resetFilters"
              >
                <i class="fas fa-undo mr-2" />
                Réinitialiser
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Repositories and Drivers Section -->
      <div
        v-for="repository in topDrivers"
        :key="repository.repository_id"
        class="mb-4"
      >
        <b-card
          no-body
          class="shadow-sm border-0"
        >
          <b-card-header class="bg-white border-bottom-0">
            <h3>{{ repository.repository_name }}</h3>
          </b-card-header>
          <b-card-body class="pt-0">
            <b-row class="driver-stack">
              <b-col
                v-for="(driver, driverIndex) in repository.top_drivers"
                :key="driver.driver_id"
                cols="12"
                lg="6"
              >
                <b-card
                  no-body
                  class="h-100 driver-card border-0 shadow-sm"
                >
                  <b-card-body>
                    <!-- Driver Header -->
                    <div class="driver-top">
                      <div class="driver-info">
                        <div class="driver-avatar">
                          {{ getInitial(driver.driver_name) }}
                        </div>
                        <div class="driver-meta">
                          <div class="driver-name">
                            {{ driver.driver_name }}
                          </div>
                          <div class="driver-id">
                            ID: #{{ driver.driver_id }}
                          </div>
                        </div>
                      </div>
                      <span
                        class="rank-chip"
                        :class="getRankClass(driverIndex)"
                      >#{{ driverIndex + 1 }}</span>
                    </div>

                    <!-- Stats Pills -->
                    <div class="pill-row">
                      <span class="pill pill-green">{{ driver.delivered_orders_count }} livraisons</span>
                      <span class="pill pill-green">{{ formatPrice(driver.total_delivery_price) }} (Frais livraison)</span>
                    </div>
                    <div class="pill-row">
                      <span class="pill pill-navy">{{ driver.return_orders_count }} retours</span>
                      <span class="pill pill-navy">{{ formatPrice(driver.total_return_price) }} (Frais retour)</span>
                    </div>

                    <!-- Return Rate -->
                    <div class="return-card">
                      <div class="return-label">
                        Taux de retour
                      </div>
                      <div class="return-value">
                        {{ calculateReturnRate(driver) }}%
                      </div>
                      <div class="return-progress">
                        <div
                          class="return-bar"
                          :style="{ width: Math.min(100, calculateReturnRate(driver)) + '%' }"
                        />
                      </div>
                    </div>

                    <!-- Daily Performance -->
                    <div class="daily-section">
                      <div class="daily-title">
                        Détails quotidiens
                      </div>
                      <div class="daily-list">
                        <div
                          v-for="detail in driver.daily_stats"
                          :key="detail.date"
                          class="daily-item"
                        >
                          <div class="daily-date">
                            {{ formatDate(detail.date) }}
                          </div>
                          <div class="daily-meta">
                            <span class="pill pill-green-light">
                              {{ detail.delivered_count }} livr.
                            </span>
                            <span class="daily-amount">
                              {{ formatPrice(detail.delivery_price) }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </div>
    </b-container>
  </div>
</template>

<script>
import {
  BCard,
  BSpinner,
  BFormDatepicker,
  BFormGroup,
  BRow,
  BCol,
  BButton,
  BContainer,
  BCardBody,
  BCardHeader,
} from 'bootstrap-vue'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  name: 'Dashboard',

  components: {
    BCard,
    BSpinner,
    BFormDatepicker,
    BFormGroup,
    BRow,
    BCol,
    BButton,
    BContainer,
    BCardBody,
    BCardHeader,
  },

  data() {
    return {
      isLoading: false,
      user: {},
      topDrivers: [],
      fromDate: null,
      toDate: null,
    }
  },

  computed: {
    getDateRangeText() {
      if (this.fromDate && this.toDate) {
        return `pour la période du ${this.formatDate(this.fromDate)} au ${this.formatDate(this.toDate)}`
      }
      return 'pour le mois en cours'
    },
  },

  created() {
    this.user = storeAuth.state.user
    if (this.user.role === 'admin') {
      this.fetchTopDrivers()
    }
  },

  methods: {
    calculateReturnRate(driver) {
      const total = driver.delivered_orders_count + driver.return_orders_count
      if (total === 0) return '0.00'
      return ((driver.return_orders_count / total) * 100).toFixed(2)
    },

    calculateAverageReturnPrice(driver) {
      if (driver.return_orders_count === 0) return this.formatPrice(0)
      const average = driver.total_return_price / driver.return_orders_count
      return this.formatPrice(average)
    },

    calculateAverageDeliveryPrice(driver) {
      if (driver.delivered_orders_count === 0) return this.formatPrice(0)
      const average = driver.total_delivery_price / driver.delivered_orders_count
      return this.formatPrice(average)
    },

    formatPrice(price) {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND',
      }).format(price || 0)
    },

    async fetchTopDrivers() {
      try {
        this.isLoading = true
        const params = new URLSearchParams()
        if (this.fromDate) params.append('from_date', this.fromDate)
        if (this.toDate) params.append('to_date', this.toDate)

        // Fix URL construction
        const baseUrl = '/api/kpi/top-drivers'
        const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl
        const { data } = await axios.get(url)
        this.topDrivers = data
      } catch (error) {
        console.error('Error fetching top drivers:', error)
        // Enhanced error logging
        if (error.response) {
          console.error('Error response:', error.response.data)
          console.error('Status:', error.response.status)
        }
        this.$bvToast.toast('Erreur lors du chargement des données', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isLoading = false
      }
    },

    applyFilters() {
      this.fetchTopDrivers()
    },

    resetFilters() {
      this.fromDate = null
      this.toDate = null
      this.fetchTopDrivers()
    },

    formatDate(dateString) {
      if (!dateString) return ''
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date(dateString))
    },
    getInitial(name) {
      if (!name) return '?'
      return name.trim().charAt(0).toUpperCase()
    },
    getRankClass(index) {
      if (index === 0) return 'rank-gold'
      if (index === 1) return 'rank-silver'
      if (index === 2) return 'rank-bronze'
      return 'rank-default'
    },
  },
}
</script>

<style lang="scss">

.dashboard-container {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 1.5rem;
}

.driver-stack {
  row-gap: 1.25rem;
}

.date-picker-custom {
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;

  &:focus-within {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}

.driver-card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  border-radius: 18px;
  background: #fdfefe;
  border: 1px solid #e5e7eb;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
  margin-bottom: 10px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  }
}

.driver-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.driver-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.driver-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0a2634, #ff9f43);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.18);
}

.driver-meta {
  display: flex;
  flex-direction: column;
}

.driver-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: #2c3e50;
}

.driver-id {
  font-size: 0.85rem;
  color: #8a94a6;
}

.rank-chip {
  min-width: 38px;
  height: 32px;
  padding: 0 10px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #fff;
  font-size: 0.9rem;
}

.rank-gold { background: linear-gradient(135deg, #ffd700, #ffb800); color: #5c4a00; }
.rank-silver { background: linear-gradient(135deg, #c0c0c0, #a0a0a0); color: #3f3f3f; }
.rank-bronze { background: linear-gradient(135deg, #cd7f32, #b87333); color: #fff; }
.rank-default { background: #6c757d; color: #fff; }

.pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.85rem;
}

.pill-green { background: #e7f7ee; color: #169b57; }
.pill-green-light { background: #f1fbf6; color: #1a9a60; }
.pill-navy { background: #e8eef5; color: #0a2634; }

.return-card {
  margin: 14px 0 10px;
  padding: 12px 14px;
  background: #f8f9fb;
  border-radius: 12px;
  border: 1px solid #edf0f5;
}

.return-label {
  font-size: 0.9rem;
  color: #6c757d;
}

.return-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #0a2634;
  margin: 4px 0;
}

.return-progress {
  height: 8px;
  border-radius: 6px;
  background: #e9eef4;
  overflow: hidden;
}

.return-bar {
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #ff9f43, #d14343);
  transition: width 0.3s ease;
}

.daily-section {
  margin-top: 12px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 12px;
}

.daily-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: #2c3e50;
  margin-bottom: 8px;
}

.daily-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.daily-item {
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #f0f2f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.daily-date {
  color: #6c757d;
  font-size: 0.9rem;
}

.daily-meta {
  display: flex;
  gap: 8px;
  align-items: center;
}

.daily-amount {
  font-weight: 700;
  color: #0a2634;
}
</style>
